import React from 'react';
import { Icon } from '@iconify/react/dist/iconify.js';
import { useSearch } from '../../contexts/SearchContext';
import { useAuthUser } from '../../contexts/AuthUserContext';

const SearchResultsLayout = ({ 
  children, 
  searchQuery, 
  totalResults, 
  activeTab, 
  setActiveTab,
  tabConfig,
  animationStates,
  customerConfig // New prop
}) => {
  const { viewMode, toggleViewMode } = useSearch();
  const { isHeliosAdmin } = useAuthUser();

  // Filter tabs based on customerConfig
  const filteredTabs = React.useMemo(() => {
    if (!isHeliosAdmin || !customerConfig) {
      return tabConfig;
    }
    
    // For customer impersonation, show only the tabs that are supported for this customer
    return tabConfig.filter(tab => 
      customerConfig.tabs.some(customerTab => customerTab.id === tab.id)
    );
  }, [isHeliosAdmin, customerConfig, tabConfig]);

  return (
    <div className="responsive-page-layout h-full scrollable overflow-y-auto">
      {/* Header */}
      <div className={`home-title dashboard-header-responsive flex-shrink-0 transition-all duration-700 ease-out ${
        animationStates.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}>
        <div className="header-textbox">
          <Icon icon="material-symbols:search" className="header-icon" />
          Search Results
        </div>
        <div className="header-right-3">
          <div className="flex items-center gap-2 text-sm text-font-secondary whitespace-nowrap">
            <Icon icon="material-symbols:info-outline" className="text-lg" />
            <span>
              <b className="text-primary">{totalResults}</b> results for "{searchQuery}"
            </span>
          </div>
        </div>
      </div>

      {/* View Mode Toggle and Tabs */}
      <div className={`w-full flex-shrink-0 transition-all duration-500 ease-out ${
        animationStates.tabs ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}>
        <div className="flex overflow-x-auto scrollable-x p-2 -m-2">
          {/* View Mode Toggle - Only visible to helios_admin users */}
          {isHeliosAdmin && (
            <div className="flex items-center gap-2 pr-4 mr-4 border-r border-bgAccent">
              <button
                className={`flex items-center gap-1 px-4 py-2 rounded-md transition-all duration-200 ${
                  viewMode === 'admin' 
                    ? 'tag-secondary text-primary font-semibold' 
                    : 'text-font-secondary hover:text-font-primary'
                }`}
                onClick={() => toggleViewMode('admin')}
              >
                <Icon icon="mdi:administrator-outline" className="text-xl" />
                <span className={`text-sm ${viewMode === 'admin' ? 'text-font-primary' : 'text-font-secondary'}`}>Admin</span>
              </button>
              <button
                className={`flex items-center gap-2 px-4 py-2 rounded-md transition-all duration-200 ${
                  viewMode === 'client' 
                    ? 'tag-secondary text-primary font-semibold' 
                    : 'text-font-secondary hover:text-font-primary'
                }`}
                onClick={() => toggleViewMode('client')}
              >
                <Icon icon="octicon:organization-16" className="text-xl" />
                <span className={`text-sm ${viewMode === 'client' ? 'text-font-primary' : 'text-font-secondary'}`}>Client</span>
              </button>
            </div>
          )}
          
          {/* Tab Navigation */}
          {filteredTabs.map((tab) => (
            <button
              key={tab.id}
              className={`px-3 py-2 font-medium text-sm whitespace-nowrap flex items-center gap-3 ${
                activeTab === tab.id ? 'text-primary tag-secondary rounded-md' : 'text-font-secondary'
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="flex items-center gap-1">
                <Icon icon={tab.icon} className="text-xl" />
                <span className={`${activeTab === tab.id ? 'text-font-primary' : 'text-font-secondary'}`}>{tab.label}</span>
              </span>
              <span className="text-font-primary text-base font-semibold">
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Search Results Content */}
      <div className={`calls-tab-layout flex-1 flex flex-col gap-4 w-full min-h-0 transition-all duration-700 ease-out ${
        animationStates.results ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}>
        {children}
      </div>
    </div>
  );
};

export default SearchResultsLayout;
